import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react'
import { api } from '@pilot-hub/api-client'

// Re-export api for other uses
export { api }

interface ActiveMission {
  id: number
  title: string
  type: string
  status: string
  plannedStart: string
  plannedEnd?: string
  leaderId: number
  participants: MissionParticipant[]
}

interface MissionParticipant {
  userId: number
  functionId: number
  functionName: string
  vehicleId?: number
}

interface User {
  id: number
  vorname: string
  nachname: string
  rollen: string[]
  [key: string]: any
}

interface MissionContextType {
  activeMission: ActiveMission | null
  isCoPilot: boolean
  isPilot: boolean
  isLeader: boolean
  participantFunction?: string
  loading: boolean
  refetch: () => Promise<void>
}

const MissionContext = createContext<MissionContextType | null>(null)

// ---------------------------------------------------------------------------
// Auth-Helfer
// Der Access-Token laeuft nach 15 Minuten ab. Dieses Hook pollt den aktiven
// Einsatz alle 30s – ohne Token-Erneuerung laufen die Anfragen danach
// dauerhaft auf 401. Der axios-Client (api-client) hat Refresh-Logik, dieses
// Hook nutzt aber `fetch` und muss sie daher selbst mitbringen.
// ---------------------------------------------------------------------------
function authStorageKey(): string {
  return (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
}

function apiBaseUrl(): string {
  return (window as any).PILOT_HUB_CONFIG?.apiBaseUrl || '/api/v1'
}

function readAuth(): any | null {
  const raw = localStorage.getItem(authStorageKey())
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function readTokens(auth: any): { access: string | null; refresh: string | null } {
  if (!auth) return { access: null, refresh: null }
  const s = auth.state || {}
  return {
    access: auth.accessToken || auth.access_token || s.accessToken || s.access_token || null,
    refresh: auth.refreshToken || auth.refresh_token || s.refreshToken || s.refresh_token || null,
  }
}

function writeTokens(access: string, refresh?: string | null): void {
  const auth = readAuth()
  if (!auth) return
  auth.accessToken = access
  auth.access_token = access
  if (auth.state) {
    auth.state.accessToken = access
    auth.state.access_token = access
  }
  if (refresh) {
    auth.refreshToken = refresh
    auth.refresh_token = refresh
    if (auth.state) {
      auth.state.refreshToken = refresh
      auth.state.refresh_token = refresh
    }
  }
  localStorage.setItem(authStorageKey(), JSON.stringify(auth))
}

async function refreshAccessToken(): Promise<string | null> {
  const { refresh } = readTokens(readAuth())
  if (!refresh) return null
  try {
    const res = await fetch(`${apiBaseUrl()}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh })
    })
    if (!res.ok) return null
    const data = await res.json()
    if (!data?.access_token) return null
    writeTokens(data.access_token, data.refresh_token)
    return data.access_token
  } catch {
    return null
  }
}

/** fetch mit Authorization-Header - nutzt axios direkt damit Refresh-Interceptor greift. */
async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  // Use axios directly so the response interceptor can handle 401 + refresh
  const response = await api.get(url.replace(`${apiBaseUrl()}/`, ''), {
    headers: init?.headers as any
  })
  return {
    ok: true,
    status: 200,
    json: async () => response.data,
    text: async () => JSON.stringify(response.data)
  } as unknown as Response
}

/** Prüft, ob ein JWT abgelaufen ist (bzw. in <5 s abläuft). */
function tokenAbgelaufen(token: string): boolean {
  try {
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    )
    return typeof payload?.exp === 'number' ? payload.exp * 1000 <= Date.now() + 5000 : false
  } catch {
    return true
  }
}

async function fetchUserFromToken(_token: string): Promise<User | null> {
  try {
    const response = await authFetch(`${apiBaseUrl()}/auth/me`)
    if (response.ok) {
      return await response.json()
    }
  } catch {
    // Ignore errors
  }
  return null
}

export function MissionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [activeMission, setActiveMission] = useState<ActiveMission | null>(null)
  const [loading, setLoading] = useState(true)
  /** true = Token nicht (mehr) verwendbar → Polling einstellen statt 401-Spam */
  const [authProblem, setAuthProblem] = useState(false)

  const refetch = async () => {
    if (!isAuthenticated || !user) {
      setActiveMission(null)
      setLoading(false)
      return
    }

    // Kein Token vorhanden → gar nicht erst anfragen (verhindert 401-Spam)
    let { access } = readTokens(readAuth())
    if (!access) {
      setAuthProblem(true)
      setActiveMission(null)
      setLoading(false)
      return
    }

    // Abgelaufener Token (Access läuft nach 15 min ab) → vorher erneuern
    if (tokenAbgelaufen(access)) {
      const erneuert = await refreshAccessToken()
      if (!erneuert) {
        setAuthProblem(true)
        setActiveMission(null)
        setLoading(false)
        return
      }
      access = erneuert
    }

    try {
      const response = await authFetch(`${apiBaseUrl()}/einsaetze/aktiver-einsatz?nutzer_id=${user.id}`)
      if (response.ok) {
        const raw = await response.json()
        // Die API liefert deutsche Feldnamen (titel, einsatzleiter_id, ...) und
        // KEIN participants-Array -> auf die Kontext-Form normalisieren.
        setActiveMission(raw ? {
          id: raw.id,
          title: raw.titel ?? raw.title ?? '',
          type: raw.typ ?? raw.type ?? '',
          status: raw.status ?? '',
          plannedStart: raw.beginn_geplant ?? raw.plannedStart ?? '',
          plannedEnd: raw.ende_geplant ?? raw.plannedEnd,
          leaderId: raw.einsatzleiter_id ?? raw.leaderId ?? 0,
          participants: raw.teilnehmer ?? raw.participants ?? []
        } : null)
      } else {
        setActiveMission(null)
      }
    } catch (err: any) {
      // 401 = Token nicht (mehr) gültig → Polling einstellen, nicht weiter hämmern
      if (err?.response?.status === 401) setAuthProblem(true)
      // Netzwerkfehler/5xx sind KEIN "kein Einsatz": bisherigen Einsatz behalten (Verbindungsstatus zeigt den Fehler)
      const status: number | undefined = err?.response?.status
      if (status !== undefined && status < 500) setActiveMission(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Check for auth from localStorage
    const checkAuth = async () => {
      const { access } = readTokens(readAuth())
      if (access) {
        setIsAuthenticated(true)
        const userData = await fetchUserFromToken(access)
        if (userData) {
          setUser(userData)
        }
      }
      await refetch()
    }

    checkAuth()
    // Kein Polling, solange der Token nicht verwendbar ist
    if (authProblem) return
    const interval = setInterval(refetch, 30000)
    return () => clearInterval(interval)
  }, [isAuthenticated, user?.id, authProblem])

  const isLeader = activeMission?.leaderId === user?.id
  const participant = activeMission?.participants?.find(p => p.userId === user?.id)
  const isCoPilot = participant?.functionName === 'Co-Pilot'
  const isPilot = participant?.functionName === 'Pilot'

  const contextValue: MissionContextType = {
    activeMission,
    isCoPilot,
    isPilot,
    isLeader,
    participantFunction: participant?.functionName,
    loading,
    refetch
  }

  return React.createElement(MissionContext.Provider, { value: contextValue }, children)
}

export function useMissionContext() {
  const context = useContext(MissionContext)
  if (!context) {
    throw new Error('useMissionContext must be used within MissionProvider')
  }
  return context
}

/** Wie useMissionContext, liefert aber null statt eines Fehlers, wenn kein MissionProvider vorhanden ist. */
export function useOptionalMissionContext() {
  return useContext(MissionContext)
}
